import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createSelector } from '@ngrx/store';

import { TaskActions, TaskActionTypes } from './task.actions';
import { TaskModel } from '../../models/task.model';

export interface State extends EntityState<TaskModel> {
  idsFavorites: string[];
  idsNotifications: string[];
  ids: string[];
  loading: boolean;
  loadingOrder: boolean;
  selectedIdUpdateItem: string | null;
  openId: string | null;
  entities: any;
}

export const adapter: EntityAdapter<TaskModel> = createEntityAdapter<TaskModel>({
  selectId: (task: TaskModel) => task.id,
  sortComparer: false,
});

export const initialState: State = adapter.getInitialState({
  idsFavorites: [],
  idsNotifications: [],
  ids: [],
  loading: false,
  loadingOrder: false,
  selectedIdUpdateItem: null,
  openId: null,
  entities: {},
});

// Helper function to update task orders
const updateTaskOrder = (
  entities: { [key: string]: TaskModel },
  taskId: string,
  statusId: string,
  afterId?: string,
  beforeId?: string
): { [key: string]: TaskModel } => {
  const updatedEntities = { ...entities };
  const task = new TaskModel().setAttributes(updatedEntities[taskId]);
  task.statusId = statusId;

  if (afterId) {
    const afterTask = updatedEntities[afterId];
    let startOrder = afterTask.order;
    task.order = ++startOrder;

    // Update order for tasks that come after
    Object.values(updatedEntities).forEach((item: TaskModel) => {
      if (item.statusId === statusId && item.order > afterTask.order && item.id !== taskId) {
        const updatedItem = new TaskModel().setAttributes(item);
        updatedItem.order = ++startOrder;
        updatedEntities[item.id] = updatedItem;
      }
    });
  } else if (beforeId) {
    const beforeTask = updatedEntities[beforeId];
    let startOrder = beforeTask.order;
    task.order = --startOrder;

    // Update order for tasks that come before
    Object.values(updatedEntities).forEach((item: TaskModel) => {
      if (item.statusId === statusId && item.order < beforeTask.order && item.id !== taskId) {
        const updatedItem = new TaskModel().setAttributes(item);
        updatedItem.order = --startOrder;
        updatedEntities[item.id] = updatedItem;
      }
    });
  } else {
    // If no before or after task, put at the end
    const maxOrder = Math.max(
      ...Object.values(updatedEntities)
        .filter((item: TaskModel) => item.statusId === statusId)
        .map((item: TaskModel) => item.order || 0),
      0
    );
    task.order = maxOrder + 1;
  }

  updatedEntities[taskId] = task;
  return updatedEntities;
};

export function reducer(state = initialState, action: TaskActions): State {
  switch (action.type) {
    case TaskActionTypes.TaskClearAction: {
      const allowIds = [...state.idsFavorites, ...state.idsNotifications];
      const entities = state.entities;

      const filtered = Object.keys(entities)
        .filter(key => allowIds.includes(key))
        .reduce((obj, key) => {
          obj[key] = entities[key];
          return obj;
        }, {});

      return {
        ...initialState,
        idsFavorites: state.idsFavorites,
        idsNotifications: state.idsNotifications,
        entities: filtered,
      };
    }

    case TaskActionTypes.SearchFavoriteSuccessAction: {
      const newEntities = action.payload
        .filter(entity => !state.entities[entity.id])
        .reduce(
          (entities, entity) =>
            Object.assign(entities, {
              [entity.id]: entity,
            }),
          {}
        );

      return Object.assign({}, state, {
        idsFavorites: action.payload.map(entity => entity.id),
        entities: Object.assign({}, state.entities, newEntities),
      });
    }

    case TaskActionTypes.SearchNotificationSuccessAction: {
      const entitiesNew = action.payload.reduce(
        (entities, entity) =>
          Object.assign(entities, {
            [entity.id]: entity,
          }),
        {}
      );

      return Object.assign({}, state, {
        idsNotifications: action.payload.map(entity => entity.id),
        entities: Object.assign({}, state.entities, entitiesNew),
      });
    }

    case TaskActionTypes.UpdateNotificationAction: {
      const model = action.payload;
      const index = state.idsNotifications.indexOf(model.id);
      let newIds = [...state.idsNotifications];

      if (index > -1) {
        newIds = [model.id, ...newIds.slice(0, index), ...newIds.slice(index + 1)];
      } else {
        newIds = [model.id, ...newIds];
      }

      return Object.assign({}, state, {
        idsNotifications: newIds,
        entities: Object.assign({}, state.entities, { [model.id]: model }),
      });
    }

    case TaskActionTypes.ClearNotificationsAction: {
      return Object.assign({}, state, {
        entities: Object.assign({}, state.entities, { [action.payload.id]: action.payload }),
        loading: false,
      });
    }

    case TaskActionTypes.SearchSuccessAction: {
      const newEntities = action.payload.reduce(
        (entities, entity) =>
          Object.assign(entities, {
            [entity.id]: entity,
          }),
        {}
      );

      return Object.assign({}, state, {
        ids: action.payload.map(entity => entity.id),
        entities: Object.assign({}, state.entities, newEntities),
        loading: false,
      });
    }

    case TaskActionTypes.ParticleAction: {
      return Object.assign({}, state, {
        loading: true,
      });
    }

    case TaskActionTypes.ParticleSuccessAction: {
      const newEntities = action.payload.reduce(
        (entities, entity) =>
          Object.assign(entities, {
            [entity.id]: entity,
          }),
        {}
      );

      let ids = state.ids;
      ids.push(...action.payload.map(entity => entity.id));
      ids = Array.from(new Set(ids));

      return Object.assign({}, state, {
        ids: ids,
        entities: Object.assign({}, state.entities, newEntities),
        loading: false,
      });
    }

    case TaskActionTypes.FindOneAction: {
      return Object.assign({}, state, {
        loading: true,
      });
    }

    case TaskActionTypes.FindOneSuccessAction: {
      return Object.assign({}, state, {
        entities: Object.assign({}, state.entities, { [action.payload.id]: action.payload }),
        loading: false,
      });
    }

    case TaskActionTypes.OpenAction: {
      return Object.assign({}, state, {
        openId: action.payload,
      });
    }

    case TaskActionTypes.CloseAction: {
      return Object.assign({}, state, {
        openId: null,
      });
    }

    case TaskActionTypes.SaveAction: {
      return Object.assign({}, state, {
        loading: true,
      });
    }

    case TaskActionTypes.SaveSuccessAction: {
      return Object.assign({}, state, {
        entities: Object.assign({}, state.entities, { [action.payload.id]: action.payload }),
        loading: false,
      });
    }

    case TaskActionTypes.ArchiveAction: {
      return Object.assign({}, state, {
        loading: true,
      });
    }

    case TaskActionTypes.ArchiveSuccessAction: {
      const newIdsFavorites = state.idsFavorites.filter(id => id !== action.payload.id);
      const newIdsNotifications = state.idsNotifications.filter(id => id !== action.payload.id);

      return adapter.removeOne(action.payload.id, {
        ...state,
        idsFavorites: newIdsFavorites,
        idsNotifications: newIdsNotifications,
      });
    }

    case TaskActionTypes.FavoriteAction: {
      return Object.assign({}, state, {
        loading: true,
      });
    }

    case TaskActionTypes.FavoriteSuccessAction: {
      const idsFavorites = action.payload.isFavorite
        ? [...state.idsFavorites, action.payload.id]
        : state.idsFavorites.filter(id => id !== action.payload.id);

      return {
        ...state,
        idsFavorites,
        entities: {
          ...state.entities,
          [action.payload.id]: action.payload,
        },
        loading: false,
      };
    }

    case TaskActionTypes.CreateAction: {
      return Object.assign({}, state, {
        loading: true,
      });
    }

    case TaskActionTypes.CreateSuccessAction: {
      const ids = [...state.ids];
      ids.push(action.payload.id);

      const entities = updateTaskOrder(
        { ...state.entities, [action.payload.id]: action.payload },
        action.payload.id,
        action.payload.statusId,
        action.payload.afterId,
        action.payload.beforeId
      );

      return Object.assign({}, state, {
        ids,
        loading: false,
        entities,
      });
    }

    case TaskActionTypes.OrderAction: {
      return Object.assign({}, state, {
        loading: true,
        loadingOrder: true,
      });
    }

    case TaskActionTypes.OrderSuccessAction: {
      const ids = state.ids;
      const entities = updateTaskOrder(
        { ...state.entities },
        action.payload.id,
        action.payload.statusId,
        action.payload.afterId,
        action.payload.beforeId
      );

      return Object.assign({}, state, {
        ids,
        loading: false,
        loadingOrder: false,
        entities,
      });
    }

    case TaskActionTypes.TempDestroyAction: {
      const model = action.payload;
      const newEntities = { ...state.entities };
      delete newEntities[model.id];

      return Object.assign({}, state, {
        ids: state.ids.filter(id => id !== model.id),
        entities: newEntities,
      });
    }

    case TaskActionTypes.MoveToProjectAction: {
      return Object.assign({}, state, {
        loading: true,
      });
    }

    case TaskActionTypes.MoveToProjectSuccessAction: {
      const updatedTask = action.payload;

      // Удаляем задачу только из текущего списка ids, так как она больше не принадлежит этому проекту
      const newIds = state.ids.filter(id => id !== updatedTask.id);

      // Обновляем задачу в entities с новыми данными
      const newEntities = Object.assign({}, state.entities, { [updatedTask.id]: updatedTask });

      return Object.assign({}, state, {
        ids: newIds,
        entities: newEntities,
        loading: false,
      });
    }

    case TaskActionTypes.MoveToProjectFailureAction: {
      return Object.assign({}, state, {
        loading: false,
      });
    }

    default:
      return state;
  }
}

export const getEntities = (state: State) => state.entities;
export const getIds = (state: State) => state.ids;
export const getIdsFavorites = (state: State) => state.idsFavorites;
export const getIdsNotifications = (state: State) => state.idsNotifications;
export const getLoading = (state: State) => state.loading;
export const getLoadingOrder = (state: State) => state.loadingOrder;
export const getEntitiesArray = createSelector(getEntities, getIds, (entities, ids) => ids.map(id => entities[id]));
export const getEntitiesFavoritesArray = createSelector(getEntities, getIdsFavorites, (entities, ids) =>
  ids
    .map(id => entities[id])
    .sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    })
);

export const getEntitiesNotificationsArray = createSelector(getEntities, getIdsNotifications, (entities, ids) =>
  ids.map(id => entities[id])
);
export const getEntitiesArrayByStatusId = statusId =>
  createSelector(getEntitiesArray, entities => entities.filter(item => item.statusId === statusId));

export const getOpen = (state: State) => state.entities[state.openId];
export const getSelectedId = (state: State) => state.openId;
